import { LeaseNumRecSelectedIdentity } from '../../graphql/queries/votes';
import { getErrorString } from '../../../../util/normalize-errors';
import getLogger from '../../../../util/logger';
import {
  COMMAND_MODES,
  NUM_REC_LEASE_EXTEND_THRESHOLD_SEC,
} from '../../constants/command';
import { trackCommandVoteEvent } from '../../utilities/analytics';
import { NUM_REC_VOTE_DECISION } from '../../../../constants/vote-decisions';

const log = getLogger('NumRecActions');

export const NUMREC_FETCH_SELECTED_IDENTITY_REQUEST =
  'numrec/NUMREC_FETCH_SELECTED_IDENTITY_REQUEST';
export const NUMREC_FETCH_SELECTED_IDENTITY_SUCCESS =
  'numrec/NUMREC_FETCH_SELECTED_IDENTITY_SUCCESS';
export const NUMREC_VOTES_CAST_REQUEST = 'numrec/NUMREC_VOTES_CAST_REQUEST';
export const NUMREC_VOTES_CAST_SUCCESS = 'numrec/NUMREC_VOTES_CAST_SUCCESS';
export const NUMREC_VOTES_CAST_FAILURE = 'numrec/NUMREC_VOTES_CAST_FAILURE';
export const NUMREC_IDENTITY_DONE_REQUEST =
  'numrec/NUMREC_IDENTITY_DONE_REQUEST';
export const NUMREC_IDENTITY_DONE_SUCCESS =
  'numrec/NUMREC_IDENTITY_DONE_SUCCESS';
export const NUMREC_IDENTITY_DONE_FAILURE =
  'numrec/NUMREC_IDENTITY_DONE_FAILURE';
export const NUMREC_SELECT_PHOTOS = 'numrec/NUMREC_SELECT_PHOTOS';
export const NUMREC_DESELECT_PHOTOS = 'numrec/NUMREC_DESELECT_PHOTOS';
export const NUM_REC_SETTINGS_UPDATE = 'numrec/NUM_REC_SETTINGS_UPDATE';

export const numRecSettingsUpdate = ({ defaultZoomOut } = {}) => ({
  type: NUM_REC_SETTINGS_UPDATE,
  defaultZoomOut,
});

const fetchSelectedIdentityRequest = () => ({
  type: NUMREC_FETCH_SELECTED_IDENTITY_REQUEST,
});

const fetchSelectedIdentitySuccess = lease => ({
  type: NUMREC_FETCH_SELECTED_IDENTITY_SUCCESS,
  lease,
});

const castNumRecVotesRequest = () => ({
  type: NUMREC_VOTES_CAST_REQUEST,
});

const castNumRecVotesSuccess = votes => ({
  type: NUMREC_VOTES_CAST_SUCCESS,
  votes,
});

const castNumRecVotesFailure = error => ({
  type: NUMREC_VOTES_CAST_FAILURE,
  error,
});

const numRecSelectedIdentityDoneRequest = () => ({
  type: NUMREC_IDENTITY_DONE_REQUEST,
});

const numRecSelectedIdentityDoneSuccess = selectedIdentity => ({
  type: NUMREC_IDENTITY_DONE_SUCCESS,
  selectedIdentity,
});

const numRecSelectedIdentityDoneFailure = error => ({
  type: NUMREC_IDENTITY_DONE_FAILURE,
  error,
});

export const numRecSelectPhotos = numRecPhotos => ({
  type: NUMREC_SELECT_PHOTOS,
  numRecPhotos,
});

export const numRecDeselectPhotos = numRecPhotos => ({
  type: NUMREC_DESELECT_PHOTOS,
  numRecPhotos,
});

export const numRecCastVotes = ({
  selectedIdentity,
  numRecPhotos,
  vote,
  numRecVoteMutation,
  user,
}) => async dispatch => {
  log.info('numRecCastVotes :: numRecPhotos Cast: ', numRecPhotos);
  log.info('numRecCastVotes :: vote: ', vote);

  dispatch(castNumRecVotesRequest());
  try {
    // Vote on Matches:
    const votes = numRecPhotos.map(numRecPhoto => ({
      decision: vote,
      face_id: numRecPhoto.face.id,
      photo_id: numRecPhoto.photo.id,
    }));

    log.info('numRecCastVotes :: Votes: ', votes);

    await numRecVoteMutation(selectedIdentity.id, votes);

    trackCommandVoteEvent({
      mode: COMMAND_MODES.NUM_REC,
      decision: vote,
      user,
      votes,
    });
    dispatch(castNumRecVotesSuccess(votes));
  } catch (error) {
    log.error('numRecCastVotes :: error', error);
    castNumRecVotesFailure(getErrorString(error));
  }
};

export const fetchSelectedIdentity = ({
  client,
  selectedIdentityId,
}) => async dispatch => {
  dispatch(fetchSelectedIdentityRequest());

  const {
    data: { leaseNumRecSelectedIdentity },
  } = await client.query({
    query: LeaseNumRecSelectedIdentity,
    variables: {
      time: NUM_REC_LEASE_EXTEND_THRESHOLD_SEC,
      selectedIdentityIds: selectedIdentityId
        ? [selectedIdentityId]
        : undefined,
    },
    fetchPolicy: 'network-only',
  });

  dispatch(
    fetchSelectedIdentitySuccess({
      lease_id: leaseNumRecSelectedIdentity.lease_id,
      expiresAt: leaseNumRecSelectedIdentity.lease_expires_at,
      selectedIdentity: leaseNumRecSelectedIdentity.selected_identity,
    }),
  );
};

/**
 * Performs actual mutation to cast votes within our platform.
 *
 * @param {Object} opts
 * @param {Object} opts.selectedIdentity Selected Identity to be marked as done.
 * @param {function} opts.numRecSelectedIdentityMarkDoneMutation Mutation for marking a selected identity as done.
 * @param {function} opts.numRecPhotos Remaining (not voted on) numRecPhotos.
 * @param {function} opts.discardAll Whether or not to discard all remaining numRecPhotos.
 * @param {function} opts.numRecVoteMutation
 */
export const markSelectedIdentityAsDone = ({
  selectedIdentity,
  numRecSelectedIdentityMarkDoneMutation,
  numRecPhotos,
  numRecVoteMutation,
  discardAll = false,
}) => async dispatch => {
  log.info(
    'markSelectedIdentityAsDone :: Selected Identity marked as done: ',
    selectedIdentity,
  );

  dispatch(numRecSelectedIdentityDoneRequest());

  try {
    if (discardAll) {
      const votes = numRecPhotos.map(numRecPhoto => ({
        decision: NUM_REC_VOTE_DECISION.NEGATIVE,
        face_id: numRecPhoto.face.id,
        photo_id: numRecPhoto.photo.id,
      }));

      log.info(
        'markSelectedIdentityAsDone :: casting as negative votes: ',
        votes,
      );

      await numRecVoteMutation(selectedIdentity.id, votes);
    }

    await numRecSelectedIdentityMarkDoneMutation(selectedIdentity.id);
    dispatch(numRecSelectedIdentityDoneSuccess(selectedIdentity));
  } catch (error) {
    log.error(error);
    numRecSelectedIdentityDoneFailure(getErrorString(error));
  }
};
